import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/AuthContext';
import { familyAPI, trainingslogAPI } from '@/lib/api';

/* PT-Zugang zum Kraftlog (Betreiber 10.10.2026): „nur nach Check-Up mit PT-Buchung“, und der
   PT-Kunde bekommt einen EIGENEN Trainingsplan.
   Drei Dinge müssen zusammenkommen, der Server prüft jedes (backend/trainingslog/zugang.py):
     1. Check-Up eingetragen (leads.letzter_checkup) — geht gleich hier; derselbe Weg wie in der Karte
        „Check-Up“ (CheckupKarte.js; zählt je Person und Tag einmal gegen das Inklusiv-Kontingent, das CRM
        nennt sonst den fälligen Preis). Weitere Check-Ups und „Tag ändern“ stehen in jener Karte.
     2. PT-Buchung vom Team bestätigt — hier, mit einem zweiten, bewussten Klick
     3. ein Trainer hat der Person einen Fitness-Zyklus zugewiesen und freigegeben (Trainingsplaner)
   Die Karte hängt an den Lead-Rechten, nicht am Trainingsrecht: Der Vertrieb bestätigt PT-Buchungen. */

const tag = (iso) => (iso ? new Date(`${String(iso).slice(0, 10)}T12:00:00`).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '');
const heuteIso = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

function planText(stand) {
  const plaene = stand.plaene || [];
  if (!plaene.length) return 'Noch kein eigener Plan zugewiesen: Ein Trainer plant einen Fitness-Zyklus und weist ihn der Person zu (Trainingsplaner).';
  return `Eigener Plan: ${plaene.map((p) => `${p.name} (${p.laeuft ? 'läuft' : p.status === 'freigegeben' ? `${tag(p.startdatum)} bis ${tag(p.enddatum)}` : 'noch nicht freigegeben'})`).join(', ')}.`;
}

export default function KraftlogPtZugang({ leadId, onGeaendert }) {
  const { isAdminPermissions, userPermissions } = useAuth();
  const darfSchalten = isAdminPermissions || !!(userPermissions || {})['leads.edit'];
  const [stand, setStand] = useState(null);
  const [frage, setFrage] = useState(null);      // 'an' | 'aus' | 'checkup' — der zweite Klick steht noch aus
  const [bereit, setBereit] = useState(false);   // „Ja“ erst kurz nach der Rückfrage: ein Doppelklick überspringt sie sonst
  const [datum, setDatum] = useState(heuteIso);
  const [busy, setBusy] = useState(false);
  // Antworten gelten nur für den Lead und den Lauf, zu dem sie gehören (Blättern zwischen Leads)
  const lauf = useRef(0);

  const lade = useCallback(async () => {
    const nr = ++lauf.current;
    // Ohne Zugriff auf den Lead oder bei einem Fehler bleibt die Karte weg
    try { const r = await trainingslogAPI.ptStand(leadId); if (nr === lauf.current) setStand(r.data); }
    catch { if (nr === lauf.current) setStand(null); }
  }, [leadId]);
  useEffect(() => { setStand(null); setFrage(null); setBusy(false); lade(); return () => { lauf.current += 1; }; }, [lade]);

  useEffect(() => {
    setBereit(false);
    if (!frage) return undefined;
    const t = setTimeout(() => setBereit(true), 500);
    return () => clearTimeout(t);
  }, [frage]);

  const ausfuehren = async (aufruf, erfolg, fehlerText) => {
    const nr = lauf.current;
    setBusy(true);
    try {
      const r = await aufruf();
      if (nr !== lauf.current) return;
      toast.success(typeof erfolg === 'function' ? erfolg(r.data || {}) : erfolg);
    } catch (e) {
      if (nr !== lauf.current) return;
      toast.error(e?.response?.data?.detail || fehlerText);
    }
    await lade();
    if (nr !== lauf.current - 1) return;   // inzwischen ein anderer Lead: dessen Karte nicht anfassen
    setFrage(null);
    setBusy(false);
    onGeaendert?.();                       // die Zugangszeile im Reiter darunter lädt neu
  };
  const schalten = (an) => ausfuehren(
    () => (an ? trainingslogAPI.ptFreischalten(leadId) : trainingslogAPI.ptBeenden(leadId)),
    an ? 'PT-Buchung bestätigt' : 'PT-Zugang zum Kraftlog beendet', 'Nicht gespeichert');
  const checkupEintragen = () => ausfuehren(
    () => familyAPI.recordCheckup(leadId, datum),
    (d) => `${d.message || 'Check-Up eingetragen'}${d.preis_faellig > 0 ? ` · ${String(d.preis_faellig).replace('.', ',')} € fällig` : d.aus_kontingent ? ` · noch ${d.inklusive_offen} inklusive` : ''}`,
    'Check-Up nicht eingetragen');

  // Trainer und Leitung sehen den Kraftlog ohnehin — für sie gibt es hier nichts zu schalten
  if (!stand || stand.team) return null;

  let text;
  if (stand.aktiv && stand.wirksam) {
    text = `PT-Buchung bestätigt seit ${tag(stand.seit)}${stand.durch_name ? ` durch ${stand.durch_name}` : ''} (Check-Up am ${tag(stand.letzter_checkup)}). ${planText(stand)}`;
  } else if (stand.aktiv) {
    text = `PT-Buchung bestätigt seit ${tag(stand.seit)}, wirkt aber gerade nicht: ${!stand.mitglied ? 'keine laufende Mitgliedschaft' : 'kein Check-Up mehr eingetragen'}.`;
  } else if (!stand.mitglied) {
    text = 'Nur mit laufender Mitgliedschaft möglich.';
  } else if (!stand.letzter_checkup) {
    text = 'Einen eigenen Plan gibt es nur nach einem Check-Up mit PT-Buchung. Für dieses Mitglied ist noch kein Check-Up eingetragen.';
  } else {
    text = `Check-Up am ${tag(stand.letzter_checkup)} eingetragen — hat das Mitglied dabei PT gebucht? ${planText(stand)}`;
  }
  const kannAn = !stand.aktiv && stand.mitglied && !!stand.letzter_checkup;
  const kannCheckup = !stand.aktiv && stand.mitglied && !stand.letzter_checkup;
  const datumOk = /^\d{4}-\d{2}-\d{2}$/.test(datum) && datum <= heuteIso();

  return (
    <Card className="border-border/60 mb-4" data-testid="kraftlog-pt-zugang">
      <CardContent className="p-4 flex items-start gap-3 flex-wrap text-sm">
        <div className="flex-1 min-w-[16rem] space-y-1">
          <p className="font-medium">PT-Zugang zum Kraftlog</p>
          {frage === 'an' ? (
            <p className="text-amber-700 dark:text-amber-400" data-testid="kraftlog-pt-frage">
              Check-Up am {tag(stand.letzter_checkup)} gemacht und PT gebucht? Dann sieht das Mitglied in der App den Plan, den ein Trainer ihm zuweist.
            </p>
          ) : frage === 'checkup' ? (
            <div className="space-y-1.5" data-testid="kraftlog-pt-frage">
              <p className="text-amber-700 dark:text-amber-400">
                Check-Up eintragen? Er zählt gegen das Inklusiv-Kontingent des Vertrags; ist es aufgebraucht, nennt das CRM den fälligen Preis.
              </p>
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                Tag des Check-Ups
                <Input type="date" value={datum} max={heuteIso()} onChange={(e) => setDatum(e.target.value)} className="h-8 w-40" data-testid="kraftlog-pt-datum" />
              </label>
            </div>
          ) : frage === 'aus' ? (
            <p className="text-amber-700 dark:text-amber-400" data-testid="kraftlog-pt-frage">
              PT-Zugang beenden? Das Mitglied sieht seinen eigenen Plan dann nicht mehr in der App. Seine Einträge bleiben.
            </p>
          ) : (
            <p className="text-xs text-muted-foreground" data-testid="kraftlog-pt-text">{text}</p>
          )}
        </div>
        {darfSchalten && (frage ? (
          <span className="flex items-center gap-2">
            <Button size="sm" disabled={busy || !bereit || (frage === 'checkup' && !datumOk)} onClick={() => (frage === 'checkup' ? checkupEintragen() : schalten(frage === 'an'))} data-testid="kraftlog-pt-ja">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : frage === 'an' ? 'Ja, bestätigen' : frage === 'checkup' ? 'Ja, eintragen' : 'Ja, beenden'}
            </Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => setFrage(null)} data-testid="kraftlog-pt-abbrechen">Abbrechen</Button>
          </span>
        ) : stand.aktiv ? (
          <Button size="sm" variant="outline" onClick={() => setFrage('aus')} data-testid="kraftlog-pt-beenden">Beenden</Button>
        ) : kannAn ? (
          <Button size="sm" variant="outline" onClick={() => setFrage('an')} data-testid="kraftlog-pt-freischalten">PT gebucht – bestätigen</Button>
        ) : kannCheckup ? (
          <Button size="sm" variant="outline" onClick={() => { setDatum(heuteIso()); setFrage('checkup'); }} data-testid="kraftlog-pt-checkup">Check-Up eintragen</Button>
        ) : null)}
      </CardContent>
    </Card>
  );
}
